// PostHog client init for the dashboard (app/). See POSTHOG_IMPLEMENTATION_GUIDE.md §4.3.
//
// Talks to the first-party reverse proxy at /ingest (next.config.js rewrites),
// never *.posthog.com directly, so ad-blockers don't catch ingestion. No-ops
// cleanly when NEXT_PUBLIC_POSTHOG_KEY is unset (e.g. local dev without a key).
import posthog from 'posthog-js';
import { scrubPostHogEvent } from './util/redactUrl';
import { isLocalDevHost } from './util/analyticsAudience';

let started = false;

export function initPostHog() {
  if (started || typeof window === 'undefined') return posthog;
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return posthog; // no-op if unconfigured
  if (isLocalDevHost(window.location.hostname)) return posthog; // localhost never sends (fix-list #16)
  started = true;

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || '/ingest',
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || 'https://us.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: false, // we send $pageview manually on Pages-Router nav (see _app.js)
    capture_pageleave: true,
    autocapture: true,
    // ---- Consent gate (GDPR_COMPLIANCE_PLAN.md P2-1) ----
    // Init opted-OUT. PostHog captures NOTHING (no autocapture, no pageview,
    // no identify) until analytics consent is granted via setAnalyticsConsent()
    // below, which the AnalyticsConsentGate drives from the tc_consent cookie.
    opt_out_capturing_by_default: true,
    // ---- Session replay: off at init, started per user (fix-list #23) ----
    // Reverses the earlier DPIA hard-disable (P0-3 / P8-4) by owner decision.
    // Recording stays off here so anonymous marketing visitors are never
    // recorded; syncSessionRecording() (driven by PostHogIdentity) starts it
    // only for a consented, logged-in, non-customer, non-staff dashboard user.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true, // mask every <input> value by default
      maskTextSelector: '[data-ph-mask]', // opt-in extra masking hook
      maskInputOptions: { password: true, email: true },
      // Stripe card iframes are cross-origin, which rrweb never records
      // anyway; blocked explicitly as belt-and-braces.
      blockSelector: 'iframe[src*="stripe.com"], iframe[name^="__privateStripe"]',
    },
    // ---- URL capabilities never leave the browser (D18) ----
    // /reset-password?code=, /magic-login?loginToken=, /connect/*/redirect
    // ?access_token=&id_token= are bearer capabilities. Runs on EVERY event
    // ($pageview, autocapture, pageleave, heatmaps…) and rewrites every
    // URL-bearing property; drops the event rather than throw. Residual: the
    // /flags request's $initial_current_url bypasses before_send (accepted;
    // see src/util/redactUrl.js).
    before_send: scrubPostHogEvent,
  });
  // Single shared PostHog project (app/ + embed-engine/). Tag every event with
  // the source app so dashboard vs storefront traffic stays separable in one
  // project. See POSTHOG_IMPLEMENTATION_GUIDE.md §1.1.
  posthog.register({ tc_app: 'dashboard' });
  return posthog;
}

/**
 * Consent gate (GDPR plan P2-1). Flip PostHog capture on/off from the user's
 * `analytics` decision. Granting lazily inits if a key is configured (init is
 * opted-out-by-default, so this is what actually turns capture on); withdrawing
 * opts out and stops capture immediately. No-ops cleanly when no key is set.
 */
export function setAnalyticsConsent(granted) {
  if (typeof window === 'undefined') return;
  if (granted) {
    initPostHog();
    if (!started) return; // no key configured → nothing to opt into
    try {
      posthog.opt_in_capturing();
    } catch {}
  } else if (started) {
    try {
      posthog.opt_out_capturing();
    } catch {}
  }
}

const STAFF_EMAILS = ['admin@timbercloud.com', 'demo@timbercloud.com'];

/** Whether this session should be recorded: consented shop user, not staff. */
export function shouldRecordSession({ analytics, userId, role, email } = {}) {
  if (!analytics || !userId) return false;
  if (!role || role === 'customer') return false;
  return !STAFF_EMAILS.includes(String(email || '').trim().toLowerCase());
}

/**
 * Start or stop session replay to match shouldRecordSession(). Init keeps
 * recording disabled, so nothing is recorded until this starts it.
 */
export function syncSessionRecording(state) {
  if (!started) return;
  try {
    if (shouldRecordSession(state)) {
      if (!posthog.sessionRecordingStarted()) posthog.startSessionRecording();
    } else if (posthog.sessionRecordingStarted()) {
      posthog.stopSessionRecording();
    }
  } catch {}
}

export default posthog;
